import { VB_MODE } from "@/constants";
import {
  AssetService,
  FlagService,
  PresentationService,
  PublicVisualizationService,
  VisualizationService,
} from "@/services";
import type {
  GetAssetCategoriesResponse,
  GetContentTypesResponse,
  GetFlagsResponse,
  GetMediaItemsResponse,
  GetTagsResponse,
} from "@/services/types";

type FetchAllDataResponses = [
  Promise<GetContentTypesResponse>,
  Promise<GetTagsResponse>,
  Promise<GetMediaItemsResponse>,
  Promise<GetAssetCategoriesResponse>,
  Promise<GetFlagsResponse>,
];

export async function fetchVisualBuilderData(params: {
  propertyId: number;
  experienceId: number;
  mode: VB_MODE;
  query: unknown;
  mediaItemId?: number;
}) {
  const { mode, propertyId, experienceId, query, mediaItemId } = params;

  // Determine which services to use based on mode
  let promises: unknown[] = [];
  if (mode === VB_MODE.PRESENTATION) {
    promises = [
      PresentationService.getContentTypes(propertyId, query),
      PresentationService.getTags(propertyId, query),
      PresentationService.getMediaItems(propertyId, query),
      AssetService.getAssetCategories(Number(propertyId)) as unknown as Promise<GetAssetCategoriesResponse>,
      FlagService.getFlags(),
    ];
  }

  if (mode === VB_MODE.VISUALIZATION) {
    promises = [
      VisualizationService.getContentTypes(propertyId, experienceId, query),
      VisualizationService.getTags(propertyId, experienceId, query),
      VisualizationService.getMediaItems(propertyId, experienceId, query),
      AssetService.getAssetCategories(Number(propertyId)) as unknown as Promise<GetAssetCategoriesResponse>,
      FlagService.getFlags(),
    ];
  }

  if (
    mode === VB_MODE.PUBLIC_VISUALIZATION ||
    mode === VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION ||
    mode === VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION
  ) {
    promises = [
      PublicVisualizationService.getContentTypes(propertyId, experienceId, query, mediaItemId),
      PublicVisualizationService.getTags(propertyId, experienceId, query, mediaItemId),
      PublicVisualizationService.getMediaItems(propertyId, experienceId, query, mediaItemId),
      AssetService.getAssetCategories(Number(propertyId)) as unknown as Promise<GetAssetCategoriesResponse>,
      PublicVisualizationService.getFlags(),
    ];
  }

  // Fetch all required data
  const [
    {
      resp: { data: contentTypes },
    },
    {
      resp: { data: tags },
    },
    {
      resp: { data: mediaItems },
    },
    {
      resp: { data: assetCategories },
    },
    {
      resp: { data: flags },
    },
  ] = await Promise.all(promises as FetchAllDataResponses);

  return {
    contentTypes,
    tags,
    mediaItems,
    assetCategories,
    flags,
  };
}
